<template>
  <section class="otp-verification-modal">
    <article>
      <smd-btn type="button"
               icon="smd-icon-action-close"
               class="close-btn"
               @click="closeModal"/>
      <h1 class="otp-verification-modal__Title m-b-75">{{ header }}</h1>
      <p class="Paragraph1 reset-m">{{ subHeader }}</p>
      <p class="Title3">{{ sentTo }}</p>

      <form class="m-t-40" @submit.prevent="verify">
        <div class="smd-form__row no-flex justify-content-center">
          <div class="smd-form__col">

            <smd-otp-input-text v-model="verificationCode"
                                :ref="$options.verificationInputRef"
                                class="align-self-center"/>

            <!--            <button type="button"-->
            <!--                    class="clear"-->
            <!--                    @click.prevent="clearVerificationCode">-->
            <!--              Clear-->
            <!--            </button>-->
          </div>
        </div>

        <div v-if="error" class="smd-form__row">
          <div class="smd-form__col">
            <p v-if="error" class="error">{{ error }}</p>
          </div>
        </div>

        <!--        <p class="form-text">Code has been sent to your mobile.</p>-->

        <div class="buttons m-t-75">
          <smd-btn class="otp-verification-modal__submit-btn" section="4"
                   variant="blue-enabled"
                   type="submit"
                   :is-loading="submitting"
                   :disabled="submitDisabled">
            <text-string label-id="txt575"/>
          </smd-btn>
        </div>
      </form>

      <vue-hcaptcha
        ref="hcaptcha"
        :sitekey="$options.siteKey"
        size="invisible"
        @error="onHcaptchaError"
        @verify="onHcaptchaVerify"
      />

      <div v-if="resendRequestBody" class="resend">
        <p class="Paragraph1 gray-400">Didn't received the code?</p>
        <span class="Paragraph1" @click.prevent="resend">
          Resend Code
        </span>
      </div>
    </article>
  </section>
</template>

<script>
  import {isEmpty} from '@/utils';
  import {bus} from '@/main';
  import {CLOSE_OTP_EVENT, OTP_CLOSED_EVENT} from '@/constants/bus';
  import hcaptchaMixin from '@/mixins/hcaptcha-mixin';
  import {logger, redact} from '@/utils/logger';

  export default {
    name: 'OtpVerificationModal',
    mixins: [
      hcaptchaMixin,
    ],
    props: {
      header: {
        type: String,
        default: 'Enter OTP',
      },
      subHeader: {
        type: String,
        default: 'Enter the 6-digit code sent to',
      },
      sentTo: {
        type: String,
        required: true,
      },
      /**
       * This is used in case of for example,
       * User tried to request for the code initially, server sends the code to mobile
       *   but user closes the otp modal
       * Then tries to request again but the server returns a response that the last code is still available
       * In this case, upon opening the modal, ui should show that the last code is still available
       *   using this prop
       */
      initialError: {
        type: String,
        default: null,
      },
      initialVerifyToken: {
        type: String,
        default: null,
      },
      resendRequestBody: {
        type: Object,
        default: null,
      },
      resendVerifyTokenKey: {
        type: String,
        default: 'verifytoken',
      },
      verifyApiService: {
        type: Function,
        required: true,
      },
      resendApiService: {
        type: Function,
        default: null,
      },
      successResendCodes: {
        type: Array, // array of numbers (result ids)
        default: null,
      },
      /**
       * [result code on api]: 'error message'
       */
      failedResendCodes: {
        type: Object,
        default: null,
      },
      successVerifyCodes: {
        type: Array, // array of numbers (result ids)
        required: true,
      },
      /**
       * [result code on api]: 'error message'
       */
      failedVerifyCodes: {
        type: Object,
        required: true,
      },
      resendImmediately: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        submitting: false,
        submitAttempted: false,
        verificationCode: '',
        error: this.initialError,
        verifyToken: this.initialVerifyToken,
      };
    },
    computed: {
      submitDisabled() {
        return this.submitting || isEmpty(this.verificationCode) || this.verificationCode.replace(' ', '').length < this.$options.verificationMaxLength;
      },
    },
    watch: {
      initialVerifyToken(newVal) {
        this.verifyToken = newVal;
      },
    },
    created() {
      document.body.style.overflow = 'hidden';
    },
    mounted() {
      if (this.resendImmediately) {
        this.resend();
      }
    },
    methods: {
      closeModal() {
        document.body.style.overflow = '';
        bus.$emit(OTP_CLOSED_EVENT);
        bus.$emit(CLOSE_OTP_EVENT);
      },
      clearVerificationCode() {
        this.$refs[this.$options.verificationInputRef].clearInput();
      },
      triggerCaptcha() {
        this.$refs.hcaptcha.execute();
      },
      postHcaptchaVerify() {
        this.resend();
      },
      async resend() {
        this.submitting = true;

        if (!this.validateToken) {
          this.triggerCaptcha();
          return;
        }

        this.error = null;
        this.verificationCode = '';

        const payload = {
          ...this.resendRequestBody,
          captchatoken: this.validateToken,
          agent: 'web3',
        };

        try {
          const {data} = await this.resendApiService(payload);

          this.submitting = false;
          this.handleResendResult(data);
        } catch (err) {
          this.submitting = false;
          this.error = 'Requesting for code failed.';
        } finally {
          // Treat captcha tokens as single-use: never resend one after the resend endpoint has seen it.
          this.validateToken = null;
        }
      },
      handleResendResult(responseData) {
        logger.info('OTP resend response', {
          result: responseData.result,
          status: responseData.status,
          resendVerifyTokenKey: this.resendVerifyTokenKey,
          tokenAtConfiguredKey: redact(responseData[this.resendVerifyTokenKey]),
          tokenAtClaimVerifyKey: redact(responseData.claimverifytoken),
          tokenAtVerifyKey: redact(responseData.verifytoken),
          responseKeys: Object.keys(responseData),
        });

        if (this.successResendCodes.includes(responseData.result)) {
          this.verifyToken = responseData[this.resendVerifyTokenKey];
          if (!this.verifyToken) {
            logger.error('Resend success but no verifyToken found at expected key', {
              expectedKey: this.resendVerifyTokenKey,
              availableKeys: Object.keys(responseData),
            });
          }
        } else if (this.failedResendCodes[responseData.result]) {
          this.error = this.failedResendCodes[responseData.result];
        } else if (responseData.status === -9) {
          this.error = 'Unable to reach mobile number at this time. Please try again later.';
        } else {
          this.error = 'Requesting for code failed.';
        }
      },
      async verify() {
        this.error = null;
        this.submitting = true;

        logger.info('OTP verify attempt', {
          verifyToken: redact(this.verifyToken),
          initialVerifyToken: redact(this.initialVerifyToken),
          hasCode: !!this.verificationCode,
          apiService: this.verifyApiService && this.verifyApiService.name || 'unknown',
        });

        if (!this.verifyToken) {
          logger.error('OTP verify called with null verifyToken', {
            initialVerifyToken: redact(this.initialVerifyToken),
            apiService: this.verifyApiService && this.verifyApiService.name || 'unknown',
          });
        }

        try {
          const details = {
            code: this.verificationCode,
            verifytoken: this.verifyToken,
          };
          const {data} = await this.verifyApiService(details);

          this.submitting = false;

          if (data.result === 1) {
            this.handleVerify(data);
          } else {
            throw new Error();
          }
        } catch (err) {
          logger.error('OTP verify failed', {
            verifyToken: redact(this.verifyToken),
          }, err);
          this.submitting = false;
          this.error = 'Verification failed.';
        }
      },
      handleVerify({result, accesstoken}) {
        if (this.successVerifyCodes.includes(result)) {
          this.emitVerifySuccess(accesstoken);
        } else if (this.failedVerifyCodes[result]) {
          this.error = this.failedVerifyCodes[result];
        } else {
          this.error = 'Verification failed.';
        }
      },
      emitVerifySuccess(accessToken) {
        document.body.style.overflow = '';
        this.$emit('verify-success', accessToken);
      },
    },
    verificationMaxLength: 6,
    verificationInputRef: 'verificationInputRef',
  }
</script>

<style lang="scss" scoped>
  .otp-verification-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 16px;
    overflow-y: auto;
    z-index: 999999;
    background-color: rgba(33, 33, 33, 0.8);

    article {
      display: flex;
      justify-content: center;
      flex-direction: column;
      width: 100%;
      max-width: 480px;
      margin: 40px auto;
      padding: 40px;
      text-align: center;
      border-radius: 20px;
      background-color: color(white, brand);
    }

    .close-btn {
      display: block;
      margin-left: auto;
      border: none;
      outline: none;
      background: none;
      cursor: pointer;
    }

    .subheader {
      margin-bottom: 8px;
    }

    .sent-to {
      color: inherit;
      font-weight: 500;
      margin-bottom: 24px;
    }

    .clear {
      display: block;
      margin: 8px auto 0 auto;
      border: none;
      outline: none;
      background: none;
      color: color(blue, brand);
    }

    .buttons {
      margin-bottom: 16px;
    }

    .form-text {
      text-align: center;
      margin-bottom: 16px;
    }

    .resend {
      p {
        display: inline-block;
        margin-right: 8px;
      }

      span {
        cursor: pointer;
        display: inline-block;
        border: none;
        outline: none;
        background: none;
        height: unset;
        min-height: unset;
        min-width: unset;
        padding: 0;
        color: color(blue, brand);
        margin: 0 auto;
        font-weight: 500;
        text-transform: unset;
      }
    }

    @media (max-width: 768px) {
      padding: 0;
      background-color: color(white, brand);

      article {
        height: 100%;
        margin: auto;
        padding: 24px;
        max-width: unset;
        border-radius: unset;
      }

      .close-btn {
        margin-left: unset;
        margin-right: auto;
        margin-bottom: auto;
      }

      .resend {
        margin-top: auto;
      }
    }
  }

  .otp-verification-modal__Title {
    font-family: $D-DIN-Bold;
    font-size: 30px;
  }

  .otp-verification-modal__submit-btn {
    min-width: 120px !important;
  }
</style>
